'use client';
import React, { useEffect, useRef, useState } from 'react';
import { Tabs, TabsHeader, TabsBody, Tab, TabPanel } from '@material-tailwind/react';

const workData = [
    {
        label: 'AI Strategy and Roadmap',
        value: 'ai-strategy',
        title: 'From AI Ambition to a Deployable Roadmap',
        desc: 'Before building anything, your organization needs clarity on where AI creates real value, what is technically feasible, and how to sequence the work. We help enterprise and mid-market leadership teams move from broad AI interest to a specific, prioritized plan they can act on.',
        list: [
            'Identify high-impact AI use cases across business functions and workflows',
            'Assess data, systems, and operational readiness for AI deployment',
            'Define AI roadmap, architecture, governance, and measurable success criteria',
            'Prioritize quick wins versus long-term transformation initiatives',
            'Create an execution plan aligned with your budget, risk tolerance, and business goals',
        ],
    },
    {
        label: 'AI Agents and Automation',
        value: 'custom-ai',
        title: 'Intelligent Automation That Handles Complex Work',
        desc: 'Modern AI agents go beyond simple chatbots. They can reason, plan, and execute multi-step tasks across your existing systems. We design, build, and deploy agentic AI systems that automate high-volume, judgment-intensive workflows across operations, finance, customer service, and beyond.',
        list: [
            'Design multi-agent architectures for complex, parallel business processes',
            'Build LLM-powered agents integrated with your existing enterprise tools and APIs',
            'Automate document processing, approvals, data extraction, and reporting workflows',
            'Deploy RAG-based systems that give agents access to your internal knowledge',
            'Monitor, evaluate, and continuously improve agent performance in production',
        ],
    },
    {
        label: 'Enterprise Modernization',
        value: 'data-engineering',
        title: 'Replace Legacy Constraints with Scalable Modern Architecture',
        desc: 'Outdated systems slow every digital initiative. We help enterprises migrate from legacy platforms to cloud-native, API-driven architectures that can support AI, scale with growth, and integrate with modern tooling. We manage complexity so your teams keep operating throughout the transition.',
        list: [
            'Audit existing systems, dependencies, and technical debt before migration',
            'Design target-state architecture aligned with cloud, security, and business requirements',
            'Execute phased migration with minimal disruption to ongoing operations',
            'Refactor or rebuild core systems as composable, scalable services',
            'Integrate AI capabilities into modernized platforms from day one',
        ],
    },
    {
        label: 'Data Engineering',
        value: 'enterprise-software',
        title: 'The Data Foundation Your AI Initiatives Require',
        desc: 'Most AI projects stall not because of model quality but because of data problems. We build the pipelines, warehouses, governance frameworks, and AI-ready infrastructure that organizations need before serious AI deployment can succeed.',
        list: [
            'Audit data sources, quality, accessibility, and gaps across your organization',
            'Design and build modern data pipelines, lakes, and warehouses for AI workloads',
            'Implement data governance, lineage tracking, and access control frameworks',
            'Create feature stores and vector databases for ML and semantic search applications',
            'Prepare structured and unstructured data for LLM fine-tuning and RAG systems',
        ],
    },
    {
        label: 'Product Engineering',
        value: 'agentic-ai',
        title: 'Build Products That Are Intelligent From the Ground Up',
        desc: 'Whether you are adding AI features to an existing product or building a new AI-native platform, we provide the full engineering capability to move from concept to production. We work with founders, product leaders, and enterprise innovation teams as a technical delivery partner.',
        list: [
            'Define product architecture, technology stack, and AI integration strategy',
            'Build AI recommendation engines, intelligent search, and personalization systems',
            'Develop custom LLM interfaces, copilots, and embedded AI features',
            'Deliver full-stack web, mobile, and API development with AI at the core',
            'Support go-to-market with scalable, secure, and maintainable engineering foundations',
        ],
    },
];

const UsaWorkTogether = () => {
    const [activeTab, setActiveTab] = useState('ai-strategy');

    const lastHash = useRef('');

    // Set initial tab and listen for hash changes
    const scrollToTabs = () => {
        const el = document.getElementById('usa-work-tabs');
        if (el) {
            const offset = 100; // gap from top
            const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
            window.scrollTo({ top, behavior: 'smooth' });
        }
    };
    useEffect(() => {
        lastHash.current = window.location.hash;
        const updateTabFromHash = () => {
            const hash = window.location.hash.replace('#', '');

            if (
                hash === 'ai-strategy' ||
                hash === 'custom-ai' ||
                hash === 'data-engineering' ||
                hash === 'enterprise-software' ||
                hash === 'agentic-ai' ||
                hash === 'product-development'
            ) {
                setActiveTab(hash);
                scrollToTabs();
            }
        };
        updateTabFromHash();

        const interval = setInterval(() => {
            const currentHash = window.location.hash;
            if (currentHash !== lastHash.current) {
                lastHash.current = currentHash;
                updateTabFromHash();
            }
        }, 100);

        return () => clearInterval(interval);
    }, []);

    return (
        <div className="section-py">
            <div className="container xl:container mx-auto">
                <div className="row grid lg:grid-cols-12 mb-8">
                    <div className="lg:col-span-12 gap-4 grid text-center mb-6">
                        <div>
                            <div className="gradient-border mb-2">
                                <span className="gradient-text">What We Can Discuss</span>
                            </div>
                        </div>
                        <h2 className="text-primary text-center">What Can We Work On Together?</h2>
                        <div className="text-white fw-700">
                            Every conversation with Deep starts with your priorities. Below are the five solution areas Moweb
                            delivers for{' '}
                            <div>
                                enterprise and mid-market organizations -- explore each to understand what a real engagement looks
                                like.
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row grid lg:grid-cols-12">
                    <div id="usa-work-tabs" className="lg:col-span-12 grid">
                        <Tabs
                            value={activeTab}
                            key={activeTab}
                            orientation="vertical"
                            className="service-tabs-header"
                            animate={{ mount: false, unmount: false }}
                        >
                            <div className="flex flex-col md:flex-row lg:flex-row w-full lg:h-[550px] md:h-[550px] h-auto">
                                <TabsHeader className="w-[26rem] service-offered-tabs">
                                    {workData.map(({ label, value }) => (
                                        <Tab
                                            className="service-tab-size"
                                            activeClassName="service-active-li"
                                            key={value}
                                            value={value}
                                            onClick={() => setActiveTab(value)}
                                        >
                                            {label}
                                        </Tab>
                                    ))}
                                </TabsHeader>
                                <TabsBody className="services-offered-bg transition-none">
                                    {workData.map(({ value, title, desc, list }) => (
                                        <TabPanel
                                            key={value}
                                            value={value}
                                            className="transition-none p-7 service-offered-listing"
                                        >
                                            <h2 className="text-primary mb-4">{title}</h2>
                                            <p className="fs-md text-[#D7DEE5] mb-4">{desc}</p>
                                            <ul className="list-disc list-inside mb-4">
                                                {list.map((item, index) => (
                                                    <li className="fs-md text-[#D7DEE5] mb-2" key={index}>
                                                        {item}
                                                    </li>
                                                ))}
                                            </ul>
                                        </TabPanel>
                                    ))}
                                </TabsBody>
                            </div>
                        </Tabs>
                    </div>
                </div>
                <span id="testimonials"></span>
            </div>
        </div>
    );
};

export default UsaWorkTogether;
